import { combineReducers } from 'redux';
import { createReducer } from '@reduxjs/toolkit';

import * as path from 'lib/widget/reducers/path';

import {
  WIDGET_CLEAR,
  WIDGET_CLEAR_BY_IDS,
  WIDGET_LOAD,
  WIDGET_LOAD_FAIL,
  WIDGET_CHANGE_VALUE,
  WIDGET_UPDATE_VALUE,
  WIDGET_UPDATE_START,
  WIDGET_UPDATE_SUCCESS,
  WIDGET_UPDATE_FAIL,
  WIDGET_SET_ERRORS,
  PROFILE_IMAGES_UPDATE,
} from '../actions';

function removeWidgets(ids, state) {
  const stateIds = Object.keys(state);
  const idsToKeep = stateIds.filter((stateId) => !ids.includes(stateId));

  const newState = {};
  for (const id of idsToKeep) {
    newState[id] = state[id];
  }

  return newState;
}

export function loaded(state = false, action) {
  switch (action.type) {
    case WIDGET_CLEAR:
      return false;
    case WIDGET_CLEAR_BY_IDS:
    case WIDGET_LOAD:
      return true;
    default:
      return state;
  }
}

export function failed(state = false, action) {
  switch (action.type) {
    case WIDGET_CLEAR:
    case WIDGET_CLEAR_BY_IDS:
    case WIDGET_LOAD:
      return false;
    case WIDGET_LOAD_FAIL:
      return true;
    default:
      return state;
  }
}

export const widgetsData = createReducer({}, (builder) => {
  builder
    .addCase(WIDGET_CLEAR, () => ({}))
    .addCase(WIDGET_CLEAR_BY_IDS, (state, action) =>
      removeWidgets(action.ids, state),
    )
    .addCase(WIDGET_LOAD, (state, action) => {
      return Object.keys(action.widgets).reduce(
        (nextState, widgetId) => {
          const widgetData = action.widgets[widgetId];

          // If the data is null, remove it from the next state
          if (widgetData === null) {
            delete nextState[widgetId];
          } else {
            nextState[widgetId] = widgetData;
          }
          return nextState;
        },

        { ...state },
      );
    })
    .addCase(WIDGET_UPDATE_VALUE, updateSuccess)
    .addCase(WIDGET_CHANGE_VALUE, updateSuccess)
    .addCase(WIDGET_UPDATE_SUCCESS, updateSuccess);
});

function updateSuccess(state, action) {
  const pathArr = path.toArray(action.name);

  // Start item
  let prevItem = state;

  // Go down the path and update the item at the end
  pathArr.forEach((item, index) => {
    if (index === pathArr.length - 1) {
      try {
        prevItem[item] = action.value;
      } catch (e) {
        // On the start pack wizard page where we load different widget pages in one view - sometimes prevItem is undefined
        // if the step has been changed and WIDGET_CLEAR has run inbetween other actions - just return the existing state in this case
        if (e instanceof TypeError) {
          return state;
        } else {
          throw e;
        }
      }
    } else {
      prevItem = prevItem[item];
    }
  });
  return state;
}

export function lastSaveDate(state = null, action) {
  switch (action.type) {
    case WIDGET_UPDATE_SUCCESS:
    case WIDGET_CLEAR_BY_IDS:
      return new Date();
    default:
      return state;
  }
}

export function isSaving(state = {}, action) {
  switch (action.type) {
    case WIDGET_CLEAR:
      return {};
    case WIDGET_CLEAR_BY_IDS:
      return removeWidgets(action.ids, state);
    case WIDGET_UPDATE_VALUE:
    case WIDGET_UPDATE_START:
      return { ...state, [action.name]: true };
    case WIDGET_UPDATE_SUCCESS:
    case WIDGET_UPDATE_FAIL:
      return { ...state, [action.name]: false };
    default:
      return state;
  }
}

export function hasSuccess(state = {}, action) {
  switch (action.type) {
    case WIDGET_CLEAR:
      return {};
    case WIDGET_CLEAR_BY_IDS:
      return removeWidgets(action.ids, state);
    case WIDGET_CHANGE_VALUE:
    case WIDGET_UPDATE_VALUE:
      return { ...state, [action.name]: undefined };
    case WIDGET_UPDATE_SUCCESS:
      return { [action.name]: true };
    case WIDGET_UPDATE_FAIL:
      return { [action.name]: false };
    case WIDGET_SET_ERRORS:
      return {
        // Set each widget id to false, as it has an error
        ...Object.keys(action.errors).reduce(function (result, item) {
          result[item] = false;
          return result;
        }, {}),
      };
    default:
      return state;
  }
}

export function errorMessage(state = {}, action) {
  switch (action.type) {
    case WIDGET_CLEAR:
      return {};
    case WIDGET_CLEAR_BY_IDS:
      return removeWidgets(action.ids, state);
    case WIDGET_LOAD:
      return Object.keys(action.widgets).reduce(
        (nextState, widgetId) => {
          const widgetData = action.widgets[widgetId];
          if (widgetData) {
            nextState[widgetId] = widgetData.error;
          }
          return nextState;
        },
        { ...state },
      );
    case WIDGET_CHANGE_VALUE:
    case WIDGET_UPDATE_VALUE:
    case WIDGET_UPDATE_SUCCESS:
      return { ...state, [action.name]: undefined };
    case WIDGET_UPDATE_FAIL:
      return { ...state, [action.name]: action.errorMessage };
    case WIDGET_SET_ERRORS:
      return action.errors;
    default:
      return state;
  }
}

export function images(state = null, action) {
  switch (action.type) {
    case PROFILE_IMAGES_UPDATE:
      return action.images;
    default:
      return state;
  }
}

const widgetsReducer = combineReducers({
  loaded,
  failed,
  widgetsData,
  isSaving,
  lastSaveDate,
  hasSuccess,
  errorMessage,
  images,
});

// eslint-disable-next-line import/no-default-export -- FIXME: Convert to a named export https://github.com/wegotpop/popsss/wiki/Imports#default-exports
export default widgetsReducer;
